import {
  Avatar,
  Box,
  Button,
  Center,
  createStyles,
  Group,
  Loader,
  Menu,
  Table,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { Filter, formatSearchQuery, SearchRequest } from "@medplum/core";
import { useMedplum } from "@medplum/react";
import {
  ChangeEvent,
  Dispatch,
  Fragment,
  memo,
  MouseEvent,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useDispatch } from 'react-redux';
import { getFieldDefinitions } from "./SearchControlField";
import { buildFieldNameString, getOpString, renderValue } from "@medplum/react";
import axios from "axios";
import { API_ENDPOINTS } from "../../api";
import { filterByClientType, getAccessToken, getClientTypes, getIntegrationTypes, getObjectByName, platformLogo } from "../../utils";
import { ButtonComponent } from "../../components/ButtonComponent";
import { updateProfile } from '../../store/actions/ProfileActions';
import { useSelector } from "react-redux";
import { AppState } from "../../store/types/appState";
import { RedirectingModal } from "../../components/RedirectingModal";
import { useLocation } from "react-router-dom";

export class SearchChangeEvent extends Event {
  readonly definition: SearchRequest;

  constructor(definition: SearchRequest) {
    super("change");
    this.definition = definition;
  }
}

export class SearchLoadEvent extends Event {
  readonly response: any;

  constructor(response: any) {
    super("load");
    this.response = response;
  }
}

export class SearchClickEvent extends Event {
  readonly resource: any;
  readonly browserEvent: MouseEvent;

  constructor(resource: any, browserEvent: MouseEvent) {
    super("click");
    this.resource = resource;
    this.browserEvent = browserEvent;
  }
}

export interface SearchControlProps {
  search: SearchRequest;
  userConfig?: any;
  checkboxesEnabled?: boolean;
  hideToolbar?: boolean;
  hideFilters?: boolean;
  onLoad?: (e: SearchLoadEvent) => void;
  onChange?: (e: SearchChangeEvent) => void;
  onClick?: (e: SearchClickEvent) => void;
  onAuxClick?: (e: SearchClickEvent) => void;
  onNew?: () => void;
  onExport?: () => void;
  onExportCsv?: () => void;
  onExportTransactionBundle?: () => void;
  onDelete?: (ids: string[]) => void;
  onPatch?: (ids: string[]) => void;
  onBulk?: (ids: string[]) => void;
  setVisible: Dispatch<SetStateAction<boolean>>;
}

interface SearchControlState {
  searchResponse?: any;
  selected: { [id: string]: boolean };
  fieldEditorVisible: boolean;
  filterEditorVisible: boolean;
  filterDialogVisible: boolean;
  exportDialogVisible: boolean;
  filterDialogFilter?: Filter;
  filterDialogSearchParam?: any;
}

const useStyles = createStyles((theme) => ({
  root: {
    maxWidth: "100%",
    overflow: "auto",
    textAlign: "left",
    marginBottom: "20px",
  },

  table: {
    cursor: "pointer",
    marginTop: "30px",
  },

  tr: {
    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[7]
          : theme.colors.gray[0],
    },
  },

  th: {
    padding: "0 !important",
  },

  control: {
    width: "100%",
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,

    "&:hover": {
      backgroundColor:
        theme.colorScheme === "dark"
          ? theme.colors.dark[6]
          : theme.colors.gray[0],
    },
  },
  icon: {
    width: 21,
    height: 21,
    borderRadius: 21,
  },
  launchButton: {
    display: "flex",
    justifyContent: "flex-start",
  },
  platformLogo: {
    marginRight: "10px",
    width: "25px",
    height: "25px",
  },
}));

/**
 * The SearchControl component represents the embeddable search table control.
 * It includes the table, rows, headers, sorting, etc.
 * It does not include the field editor, filter editor, pagination buttons.
 * @param props - The SearchControl React props.
 * @returns The SearchControl React node.
 */
export function SearchControl(props: SearchControlProps): JSX.Element {
  const { classes } = useStyles();
  const medplum = useMedplum();
  const location = useLocation();
  const currentTab = location.pathname.replace("/admin/", "");
  const dispatch = useDispatch();
  const [schemaLoaded, setSchemaLoaded] = useState(false);
  const [outcome, setOutcome] = useState<any | undefined>();
  const [patientList, setPatientList] = useState<any>([])
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [vendorName, setVendorName] = useState("")
  const logginedUser = useSelector((state: AppState) => state) as any;
  const [userProfile, setUserProfile] = useState<any | undefined>(logginedUser.app.profile);
  const { search, onLoad } = props;

  const [state, setState] = useState<SearchControlState>({
    selected: {},
    fieldEditorVisible: false,
    filterEditorVisible: false,
    exportDialogVisible: false,
    filterDialogVisible: false,
  });
  const stateRef = useRef<SearchControlState>(state);
  stateRef.current = state;

  const totalType = search.total ?? "accurate";

  const loadResults = useCallback(
    (options?: RequestInit) => {
      setOutcome(undefined);

      medplum
        .search(
          search.resourceType as any,
          formatSearchQuery({ ...search, total: totalType, fields: undefined }),
          options
        )
        .then((response) => {
          setState({ ...stateRef.current, searchResponse: response });
          if (onLoad) {
            onLoad(new SearchLoadEvent(response));
          }
        })
        .catch((reason) => {
          setState({ ...stateRef.current, searchResponse: undefined });
          setOutcome(reason);
        });
    },
    [medplum, search, totalType, onLoad]
  );

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  function handleSingleCheckboxClick(e: ChangeEvent, id: string): void {
    e.stopPropagation();

    const el = e.target as HTMLInputElement;
    const checked = el.checked;
    const newSelected = { ...stateRef.current.selected };
    if (checked) {
      newSelected[id] = true;
    } else {
      delete newSelected[id];
    }
    setState({ ...stateRef.current, selected: newSelected });
  }

  function handleAllCheckboxClick(e: ChangeEvent): void {
    e.stopPropagation();

    const el = e.target as HTMLInputElement;
    const checked = el.checked;
    const newSelected = {} as { [id: string]: boolean };
    const searchResponse = stateRef.current.searchResponse;
    if (checked && searchResponse?.entry) {
      searchResponse.entry.forEach((entry: any) => {
        if (entry.resource?.id) {
          newSelected[entry.resource.id] = true;
        }
      });
    }
    setState({ ...stateRef.current, selected: newSelected });
  }

  function isAllSelected(): boolean {
    const state = stateRef.current;
    if (
      !state.searchResponse?.entry ||
      state.searchResponse.entry.length === 0
    ) {
      return false;
    }
    for (const e of state.searchResponse.entry) {
      if (e.resource?.id && !state.selected[e.resource.id]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Emits a change event to the optional change listener.
   * @param newSearch - The new search definition.
   */
  function emitSearchChange(newSearch: SearchRequest): void {
    if (props.onChange) {
      props.onChange(new SearchChangeEvent(newSearch));
    }
  }

  /**
   * Handles a click on a order row.
   * @param e - The click event.
   * @param resource - The FHIR resource.
   */
  function handleRowClick(e: MouseEvent, resource: any): void {
    if (e.button === 2) {
      // Ignore right clicks
      return;
    }

    const isAux = e.button === 1 || e.ctrlKey || e.metaKey;

    if (!isAux && props.onClick) {
      props.onClick(new SearchClickEvent(resource, e));
    }

    if (isAux && props.onAuxClick) {
      props.onAuxClick(new SearchClickEvent(resource, e));
    }
  }

  const getPatientNameList = async () => {
    const patientList = await medplum.searchResources("Patient")
    setPatientList(patientList)
  }

  useEffect(() => {
    setSchemaLoaded(false);
    medplum
      .requestSchema(props.search.resourceType as any)
      .then(() => setSchemaLoaded(true))
      .catch(console.log);
    getPatientNameList();
  }, [medplum, props.search.resourceType]);

 
  if (!schemaLoaded) {
    return (
      <Center style={{ width: "100%", height: "100%" }}>
        <Loader />
      </Center>
    );
  }

  function removeExtensionByUrl(
    extension: ExtensionData[],
    urlToRemove: string
  ): ExtensionData[] {
    return extension?.filter((item) => item.url !== urlToRemove);
  }

  async function handleButtonClick(
    e: React.MouseEvent<HTMLButtonElement>,
    resource: any
  ) {
    e.preventDefault();
    e.stopPropagation();

    const buttonText = e.currentTarget.textContent?.toLowerCase();
    const activeLogin = getAccessToken();

    if (buttonText === "connect") {
      const integrationType = getIntegrationTypes(resource?.meta?.extension);
      const resourceType = resource?.resourceType;
      const id = resource?.id;

      setVendorName(integrationType[0]);
      setIsRedirecting(true);

      const historyBundle = await medplum.readHistory(resourceType, id);
      localStorage.setItem("activeClient", JSON.stringify(historyBundle));

      try {
        let authUrl = '';
        
        if(integrationType[0]?.toLowerCase() === "cerner") {

          const body = {
            eventType: "url",
            clientId: id,
          };

          const res = await medplum.executeBot({
            system: 'https://api-stage.healthconnect.systems/bots',
            value: 'cerner-service'
          }, body);
          if(res.statusCode === 200) {
            authUrl = res.body;
          }
        } else {
          const scope = `patient/*.* user/*.* openid fhirUser launch launch/patient`;
          const res = await axios.post(
            API_ENDPOINTS.GENERATE_URL,
            {
              clientID: id,
              scope,
            },
            {
              headers: {
                Authorization: `Bearer ${activeLogin.accessToken}`,
              },
            }
          );

          if (res.status === 200) {
            authUrl = res.data.ehrAuthURL;
          }
        }

        if(authUrl.length) {
          window.location.href = authUrl;
        }
      } catch (error) {
        console.error("Error during external authentication:", error);
        setIsRedirecting(false);
      }
    } else {
      const activeLogin = getAccessToken();
      const userProfileClone = { ...userProfile };

      if (userProfileClone) {
        const newExtension = removeExtensionByUrl(
          userProfileClone?.extension as ExtensionData[],
          `ClientApplication/${resource?.id}`
        );
        userProfileClone.extension = newExtension;
        await axios.put(
          `${API_ENDPOINTS.UPDATE_RESOURCE}/${userProfile?.id}`,
          userProfileClone,
          {
            headers: {
              Authorization: `Bearer ${activeLogin.accessToken}`,
            },
          }
        );
        setUserProfile(userProfileClone);
        dispatch(updateProfile(userProfileClone))
      }
    }
  }

  async function handleWearableButtonClick(
    e: React.MouseEvent<HTMLButtonElement>,
    clientResource: any,
    patientId: string
  ) {
    e.stopPropagation();
    localStorage.setItem('activeClient', JSON.stringify(clientResource));
    localStorage.setItem('selectedPatient',patientId)
      let vendor = "";
      const secret = clientResource.secret
      const id = clientResource.id
      let apiPayload = {
        type: "url",
        client_id: id,
        secret: secret
      }
      const integrationType = getIntegrationTypes(clientResource.meta.extension)
      if(integrationType.length > 0) {
        vendor = integrationType[0]
      }
      if(["Fitbit", "Oura", "Polar", "Google Fit", "Dexcom"].includes(vendor)) {
        setVendorName(vendor)
        setIsRedirecting(true);
       
        try {
          let botId = {}
          if(vendor === "Fitbit")  {
            botId = {
              system: 'https://api-stage.healthconnect.systems/bots',
              value: 'fit-bit-service'
            }
          } else if(vendor === "Oura") {
            botId = {
              system: 'https://api-stage.healthconnect.systems/bots',
              value: 'oura-service'
            }
          } else if(vendor === "Google Fit") {
            botId = {
              system: 'https://api-stage.healthconnect.systems/bots',
              value: 'google-fit-service'
            }
          } else if(vendor === "Dexcom") {
            botId = {
              system: 'https://api-stage.healthconnect.systems/bots',
              value: 'dexcom-service'
            }
          } else {
            botId = {
              system: 'https://api-stage.healthconnect.systems/bots',
              value: 'polar-service-new'
            }
          }
          const result = await medplum.executeBot(botId, apiPayload)
          if(result.statusCode === 301) {
            window.location.href = result.body
          } else {
            setIsRedirecting(false)
          }
        } catch (error:any) {
            console.error("Error during external authentication:", error);
            setIsRedirecting(false);
        }
      }
  }

  const checkboxColumn = props.checkboxesEnabled;
  const fields = getFieldDefinitions(search);
  const resourceType = search.resourceType;
  const lastResult = state.searchResponse;
  const entries = lastResult?.entry;
  const resources = entries?.map((e: any) => e.resource);

  const getPatientName = (patientData: any) => {
    let name = "--";
    if(patientData?.length > 0) {
        patientData.forEach((res:any)=>{
            if(res?.use === "official") {
                name = res?.text || res?.given[0]
            }
        })
    }
    return name;
  };

  interface ExtensionObject {
    url: string;
    valueString: string;
  }

  interface ExtensionData {
    url: string;
    extension: ExtensionObject[];
  }

  function hasTopLevelUrl(
    extension: ExtensionData[] | undefined,
    urlToCheck: string
  ): boolean {
    return !!extension && extension?.some((data) => data.url === urlToCheck);
  }

  const getConnectButton = (
    resource: any
    ) => {
      const clientTypes = getClientTypes(resource?.meta?.extension)
      const integrationType = getIntegrationTypes(resource?.meta?.extension)
      if (clientTypes?.includes("Wearable")) {
        return (
          <Menu shadow="md" width={200}>
            <Menu.Target>
                <Button 
                    variant="outline"
                    onClick={(e)=>{
                      e.stopPropagation()
                    }}
                >
                    <Box component="img" src={platformLogo[integrationType[0]?.replace(/\s/g, '')?.toLowerCase()]} className={classes.platformLogo} />
                    Connect
                </Button>
            </Menu.Target>
          <Menu.Dropdown>
            {
              patientList.map((patient:any)=>{
                return (
                  <Fragment key={patient.id}>
                    <Menu.Item 
                      onClick={(e)=>handleWearableButtonClick(e,resource, patient.id)}
                      icon={<Avatar radius="xl" />}  
                    >{getPatientName(patient.name)}</Menu.Item>
                    <Menu.Divider />
                  </Fragment>
                )
              })
            }
          </Menu.Dropdown>
          </Menu>
        )
      } else {
        return <ButtonComponent resource={resource} userProfile={userProfile} handleLaunchClick={(e)=>handleButtonClick(e,resource)} classes={classes} hasTopLevelUrl={hasTopLevelUrl}/> 
      }
  }
  const capitalize = (s:string) => (s && s[0].toUpperCase() + s.slice(1)) || ""

  let filteredData;

  if(currentTab === "ehrconnect" || currentTab === "wearconnect")  {
      if(resources) {
        filteredData = filterByClientType(resources, currentTab === "ehrconnect" ? "EHR" : "Wearable");
      }
  }
  return (
    <>
    <RedirectingModal
      isOpen={isRedirecting}
      onClose={()=>setIsRedirecting(false)}
      vendor={vendorName}
      text={"Redirecting..."}
    />
    <div className={classes.root} data-testid="search-control">
      <Table className={classes.table}>
        <thead
          style={{
            background: "var(--Gray-50, #EDEDED)",
          }}
        >
          <tr>
            {fields.map((field) => (
              <th key={field.name}>
                <Menu shadow="md" width={240} position="bottom-end">
                  <Menu.Target>
                    <UnstyledButton className={classes.control}>
                      <Group position="apart" noWrap>
                        <Text weight={500} size="sm">
                          {buildFieldNameString(field.name)}
                        </Text>
                      </Group>
                    </UnstyledButton>
                  </Menu.Target>
                </Menu>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(currentTab === "ehrconnect" || currentTab === "wearconnect" ? filteredData : resources)?.map(
            (resource: any) =>
              resource && (
                <tr
                  key={resource.id}
                  className={classes.tr}
                  data-testid="search-control-row"
                  onClick={(e) => handleRowClick(e, resource)}
                  onAuxClick={(e) => handleRowClick(e, resource)}
                >
                  {fields.map((field) => (
                    <td
                      key={field.name}
                      style={{
                        paddingTop: "18px",
                        paddingBottom: "18px",
                      }}
                    >
                      {field.name.toLowerCase() === "action" ? (
                        getConnectButton(resource)
                        // <ButtonComponent resource={resource} userProfile={userProfile} handleLaunchClick={handleLaunchClick} classes={classes} hasTopLevelUrl={hasTopLevelUrl}/> 
                      ) : field.name.toLowerCase() === "name" &&  Array.isArray(resource?.name)? (

                        getPatientName(resource?.name)
                      ) : field.name.toLowerCase() === "gender" ? 
                        capitalize(resource?.gender)
                      :(
                        renderValue(resource, field)
                      )}
                    </td>
                  ))}
                </tr>
              )
          )}
        </tbody>
      </Table>
      {resources?.length === 0 && (
        <Center style={{ height: 150 }}>
          <Text size="xl" color="dimmed">
            No results
          </Text>
        </Center>
      )}
    </div>
    </>
  );
}

export const MemoizedSearchControl = memo(SearchControl);

interface FilterDescriptionProps {
  readonly resourceType: string;
  readonly searchParams: any[];
  readonly filters?: Filter[];
}

function FilterDescription(props: FilterDescriptionProps): JSX.Element {
  const filters = (props.filters ?? []).filter((f) =>
    props.searchParams.find((p) => p.code === f.code)
  );
  if (filters.length === 0) {
    return <span>no filters</span>;
  }

  return (
    <>
      {filters.map((filter: Filter) => (
        <div key={`filter-${filter.code}-${filter.operator}-${filter.value}`}>
          {getOpString(filter.operator)}
          &nbsp;
          {/* <SearchFilterValueDisplay resourceType={props.resourceType} filter={filter} /> */}
        </div>
      ))}
    </>
  );
}
